import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { getData, postData } from '../services/request'
import LatLngMapPicker from '../components/LatLngMapPicker'
import SkillsTagInput from '../components/SkillsTagInput'
import { COUNTRIES } from '../data/countries'
import {
  formToProfilePayload,
  getLoggedInUserEmail,
  inputClass,
  labelClass,
  profileToFormState
} from './employmeProfileUtils'
import CvFileUpload from './CvFileUpload'

const JobSeekerProfilePage = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const [form, setForm] = useState({
    cvUrl: '',
    cvViewUrl: '',
    fullName: '',
    contactEmail: getLoggedInUserEmail(),
    contactPhone: '',
    locationCountry: '',
    locationLat: '',
    locationLng: '',
    specialization: '',
    skills: [],
    educationSummary: '',
    yearsOfExperience: '',
    experienceSummary: ''
  })

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true)
      setError('')
      const url = `${getAPIBaseURL()}/employme/jobseeker/profile`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (reqError || !result) {
        if (result?.status === 404) {
          return
        }
        setError(result?.data?.data || 'Unable to load your profile')
        return
      }
      if (result.status === 200) {
        setForm(profileToFormState(result.data?.data || {}))
      }
    }

    fetchProfile()
  }, [])

  const onChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const saveProfile = async () => {
    setSaving(true)
    setError('')
    setInfo('')
    const url = `${getAPIBaseURL()}/employme/jobseeker/profile`
    const payload = formToProfilePayload(form)

    const { error: reqError, result } = await postData(url, {}, payload)
    setSaving(false)
    if (reqError || !result) {
      setError(result?.data?.data || 'Unable to save your profile right now.')
      return
    }
    if (result.status === 200 || result.status === 201) {
      setInfo('Profile updated.')
    } else {
      setError(result.data?.data || 'Unable to save your profile right now.')
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 animate-fade-in">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="h-6 w-40 animate-pulse-soft rounded-lg bg-slate-200" />
          <div className="mt-4 h-4 w-full animate-pulse-soft rounded bg-slate-100" />
          <div className="mt-6 space-y-4">
            <div className="h-11 w-full animate-pulse-soft rounded-xl bg-slate-100" />
            <div className="h-11 w-full animate-pulse-soft rounded-xl bg-slate-100" />
          </div>
          <p className="mt-6 text-center text-sm text-slate-500">Loading your profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl animate-fade-in">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Profile</h1>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            Keep your details up to date so employers can understand your experience quickly.
          </p>
        </div>
        <Link
          to="/jobs/profile/ai-assist"
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-[#008FCC]"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          Profile assistant
        </Link>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      {!error && info && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {info}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xl shadow-slate-200/50">
        <div className="h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-sky-500" />
        <div className="space-y-8 p-6 sm:p-8">
          <section>
            <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">Personal details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>Full name</label>
                  <input name="fullName" value={form.fullName} onChange={onChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Phone number</label>
                  <input name="contactPhone" value={form.contactPhone} onChange={onChange} className={inputClass} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={form.contactEmail}
                  readOnly
                  className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-500"
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">Location</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Country</label>
                <select name="locationCountry" value={form.locationCountry} onChange={onChange} className={inputClass}>
                  <option value="">Select country</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Map location</label>
                <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                  <LatLngMapPicker
                    value={
                      form.locationLat && form.locationLng
                        ? { lat: Number(form.locationLat), lng: Number(form.locationLng) }
                        : null
                    }
                    disabled={saving}
                    onChange={({ lat, lng }) => {
                      setForm((prev) => ({
                        ...prev,
                        locationLat: String(lat),
                        locationLng: String(lng)
                      }))
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">Professional details</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Specialization</label>
                <input
                  name="specialization"
                  value={form.specialization}
                  onChange={onChange}
                  placeholder="e.g. Software engineering, Nursing, Accounting"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Skills</label>
                <SkillsTagInput
                  skills={form.skills}
                  disabled={saving}
                  onChange={(skills) => setForm((prev) => ({ ...prev, skills }))}
                />
                <p className="mt-1.5 text-[11px] text-slate-400">Press Enter after each skill to add it.</p>
              </div>
              <div>
                <label className={labelClass}>Education</label>
                <textarea
                  name="educationSummary"
                  value={form.educationSummary}
                  onChange={onChange}
                  rows={4}
                  placeholder="Tell us about your education — schools, degrees, certifications, and relevant training..."
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div>
                <label className={labelClass}>Years of experience</label>
                <input
                  name="yearsOfExperience"
                  value={form.yearsOfExperience}
                  onChange={onChange}
                  type="number"
                  min="0"
                  placeholder="e.g. 3"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Experience</label>
                <textarea
                  name="experienceSummary"
                  value={form.experienceSummary}
                  onChange={onChange}
                  rows={5}
                  placeholder="Describe your work experience — roles, responsibilities, achievements, and industries you've worked in..."
                  className={`${inputClass} resize-y`}
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-slate-400">CV document</h2>
            <CvFileUpload
              cvUrl={form.cvUrl}
              cvViewUrl={form.cvViewUrl}
              disabled={saving}
              onCvUploaded={(url, viewUrl) => {
                setForm((prev) => ({ ...prev, cvUrl: url, cvViewUrl: viewUrl || '' }))
                setInfo('CV uploaded.')
              }}
            />
          </section>

          <div className="flex justify-end border-t border-slate-100 pt-6">
            <button
              type="button"
              disabled={saving}
              onClick={saveProfile}
              className="rounded-xl bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary-500/25 transition-all hover:bg-primary-700 hover:shadow-lg disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JobSeekerProfilePage
